import React, { useCallback } from 'react';
import { CategoryEntry } from './types';
import { useTreeCheckbox } from '../tree/hooks';
import { getItemById } from '../tree/hooks/useTreeCheckbox/helpers';
import { useCategoryNavigation } from './hooks';
import { CategoryList } from './CategoryList';

export interface CategoryTreeViewProps {
  items: CategoryEntry[];
  selectedKeys: string[];
  onChange: (selectedKeys: string[], isLeaf?: boolean) => void;
  multiple?: boolean;
  disabled?: boolean;
  maxDepth: number;
  width?: number;
}

export const CategoryTreeView = ({
  items,
  selectedKeys,
  onChange,
  multiple = false,
  disabled = false,
  maxDepth,
  width = 240,
}: CategoryTreeViewProps) => {
  const { navigationPath, onNavigate } = useCategoryNavigation({
    items,
    defaultExpandedKeys: selectedKeys,
    multiple,
  });
  const { handleCheck, getCheckState, treeMap } = useTreeCheckbox({
    items,
    checkedIds: selectedKeys,
    checkable: multiple,
    onChecked: (_checkedItems, checkedIds) => {
      onChange(checkedIds.filter((id) => id !== ''));
    },
  });

  const getCurrentLevelItems = useCallback(
    (level: number): CategoryEntry[] => {
      if (level === 0) return items;

      const parentId = navigationPath[level];
      if (!parentId) return [];

      const parentItem = getItemById(parentId, treeMap);
      return parentItem?.children || [];
    },
    [items, navigationPath, treeMap],
  );

  const handleCategorySelect = useCallback(
    (item: CategoryEntry) => {
      const checkState = getCheckState(item);
      handleCheck(item, !checkState.checked);
    },
    [getCheckState, handleCheck],
  );

  const handleCategoryClick = useCallback(
    (item: CategoryEntry, index: number) => {
      onNavigate(item, index);
      if (!multiple) {
        const isLeaf = !item.children || item.children.length === 0;
        onChange([item.id], isLeaf);
      }
    },
    [onNavigate, multiple, onChange],
  );

  const isSelected = useCallback(
    (item: CategoryEntry): boolean => {
      return navigationPath.includes(item.id);
    },
    [navigationPath],
  );

  return Array.from({ length: maxDepth }, (_, index) => {
    const levelItems = getCurrentLevelItems(index);
    return (
      <CategoryList
        key={index}
        level={index + 1}
        items={levelItems}
        onItemSelect={handleCategorySelect}
        onItemClick={(item) => handleCategoryClick(item, index)}
        getCheckState={getCheckState}
        isSelected={isSelected}
        multiple={multiple}
        disabled={disabled}
        width={width}
      />
    );
  });
};
